import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Book as BookIcon,
  Apps as AppsIcon,
  School as ClassIcon,
  People as PeopleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import { createAllPhaseMenuGroups } from '../../../../utils/createAllPhaseMenuGroups';
import axiosInstance from '../../../../utils/axiosInstance';
import config from '../../../../config.json';
import io from 'socket.io-client';
import url from '../../../../url.json';

// 初始空資料 - 將在載入後以真實資料填入
const emptyDashboardData = {
  applicationInfo: {
    id: null,
    title: '',
    lessonPackage: '',
    className: '',
    startDate: '',
    endDate: '',
    status: ''
  },
  realtimePersonal: {
    taskActivation: { totalStudents: 0, activatedStudents: 0, notActivated: [] },
    interactionStatus: { activeStudents: 0, silentStudents: 0, silentList: [] },
    stickyNoteRanking: [],
    wallUsage: { 'idea-wall': 0, 'question-wall': 0, 'cross-group': 0, 'dashboard': 0 }
  },
  realtimeGroup: {
    groupInteraction: []
  },
  postClassPersonal: {
    participationStats: [],
    questionWallStats: { totalQuestions: 0, categories: [] }
  },
  postClassGroup: {
    groupContribution: []
  }
};

export default function ClassApplicationDashboard() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [dashboardData, setDashboardData] = useState(emptyDashboardData);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ws, setWs] = useState(null);
  const activeUsersRef = React.useRef(new Set());
  const toolUseRef = React.useRef({}); // { actorId: { tool, startedAt } }
  const studentListRef = React.useRef([]); // 學生名單（用於對照）

  // 取得課程詳情、牆面節點、概覽，並計算儀表板資料
  useEffect(() => {
    if (!id) return;
    let mounted = true;
    const fetchAll = async () => {
      try {
        setLoading(true);
        const courseDispatchId = id;

        const [detailResp, overviewResp, ideaNodesResp, questionNodesResp, edgesResp] = await Promise.all([
          axiosInstance.get(`${config.getCourseDispatchDetail}${courseDispatchId}/detail`),
          axiosInstance.get(`${config.getTeacherOverview}${courseDispatchId}`),
          axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/idea`),
          axiosInstance.get(`${config.getWallNodes}${courseDispatchId}/question`),
          axiosInstance.get(`${config.getNodeEdges}${courseDispatchId}`)
        ]);

        const detail = detailResp.data?.data || {};
        const applicationInfo = detail.applicationInfo || {};
        const groups = detail.taskGroupMatrix?.groups || [];
        const tasks = detail.taskGroupMatrix?.tasks || [];
        const matrixStatus = detail.taskGroupMatrix?.matrixStatus || {};

        const overview = overviewResp.data?.data || {};
        const recentNodes = overview.recentNodes || [];

        const ideaNodes = ideaNodesResp.data?.success ? (ideaNodesResp.data.data || []) : [];
        const questionNodes = questionNodesResp.data?.success ? (questionNodesResp.data.data || []) : [];
        const allNodes = [...ideaNodes, ...questionNodes];
        const edges = edgesResp.data?.success ? (edgesResp.data.data || []) : [];

        // 應用資訊
        const appInfo = {
          id: applicationInfo.id || parseInt(courseDispatchId),
          title: applicationInfo.title || `課程派發 #${courseDispatchId}`,
          lessonPackage: applicationInfo.lessonPackage || '',
          className: applicationInfo.className || '',
          startDate: applicationInfo.created_at || '',
          endDate: '',
          status: applicationInfo.status || ''
        };

        // 個人即時：任務啟動（以有提交或已分派且有最新提交為啟動的粗略估計）
        const studentList = groups.flatMap(g => g.members || []);
        const totalStudents = studentList.length;
        // 若有被分派且該組有任務狀態為 completed，視為該組已啟動；此為簡化
        const activatedStudentIds = new Set();
        tasks.forEach(task => {
          groups.forEach(group => {
            const key = `${task.id}_${group.id}`;
            const status = matrixStatus[key]?.status;
            if (status === 'completed' || status === 'dispatched') {
              (group.members || []).forEach(m => activatedStudentIds.add(m.id));
            }
          });
        });
        const activatedStudents = activatedStudentIds.size;
        const notActivated = (studentList || [])
          .filter(s => !activatedStudentIds.has(s.id))
          .map(s => ({ id: s.id, name: s.name, reason: '尚未開始' }));

        // 個人即時：互動狀況（以最近活動作者為活躍）
        const recentActiveIds = new Set(recentNodes.map(n => n.author_id || n.Author?.id).filter(Boolean));
        const silentList = (studentList || []).filter(s => !recentActiveIds.has(s.id));
        const interactionStatus = {
          activeStudents: recentActiveIds.size,
          silentStudents: Math.max(totalStudents - recentActiveIds.size, 0),
          silentList: silentList.map(s => ({ id: s.id, name: s.name }))
        };

        // 便利貼熱度排名（以想法牆節點，互動數=連線數+回覆數）
        const edgeCountByFrom = edges.reduce((acc, e) => {
          const key = String(e.from_node_id);
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        const replyCountByParent = allNodes.reduce((acc, n) => {
          if (n.parent_node_id) {
            const key = String(n.parent_node_id);
            acc[key] = (acc[key] || 0) + 1;
          }
          return acc;
        }, {});
        const stickyBase = ideaNodes.filter(n => n.node_type !== 'reply');
        const stickyNoteRanking = stickyBase
          .map(n => {
            const idStr = String(n.id);
            const interactions = (edgeCountByFrom[idStr] || 0) + (replyCountByParent[idStr] || 0);
            return {
              id: n.id,
              content: n.content?.slice(0, 60) || n.title || '(無內容)',
              author: n.author_name || n.Author?.name || '未知',
              views: 0,
              interactions
            };
          })
          .sort((a, b) => b.interactions - a.interactions)
          .slice(0, 10);

        // 小組即時：互動與進度
        const groupInteraction = groups.map(g => {
          const memberIds = new Set((g.members || []).map(m => m.id));
          const activeMembers = Array.from(recentActiveIds).filter(id => memberIds.has(id)).length;
          const members = (g.members || []).length;
          const interactionRate = members > 0 ? Math.round((activeMembers / members) * 100) : 0;
          // 進度：該組 completed 的任務比例
          const totalTasks = tasks.length;
          let completedCount = 0;
          tasks.forEach(t => {
            const key = `${t.id}_${g.id}`;
            if (matrixStatus[key]?.status === 'completed') completedCount += 1;
          });
          const progress = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
          return {
            id: g.id,
            name: g.name,
            members,
            activeMembers,
            interactionRate,
            progress
          };
        });

        // 課後個人：參與度統計（以所有節點計數）
        const authorAgg = {};
        allNodes.forEach(n => {
          const authorId = n.author_id || n.Author?.id;
          const authorName = n.author_name || n.Author?.name || '未知';
          if (!authorId) return;
          if (!authorAgg[authorId]) authorAgg[authorId] = { id: authorId, name: authorName, posts: 0, responses: 0 };
          if (n.node_type === 'reply') authorAgg[authorId].responses += 1; else authorAgg[authorId].posts += 1;
        });
        const participationStats = Object.values(authorAgg).map(s => ({
          id: s.id,
          name: s.name,
          posts: s.posts,
          responses: s.responses,
          // 先以簡化標籤表示登入頻率/活躍度
          loginFrequency: s.posts + s.responses >= 5 ? '高' : s.posts + s.responses >= 2 ? '中' : '低',
          activityLevel: s.posts + s.responses >= 5 ? '高' : s.posts + s.responses >= 2 ? '中' : '低'
        }));

        // 課後個人：問題牆分類統計
        const questionOnly = questionNodes.filter(n => n.node_type === 'question');
        const totalQuestions = questionOnly.length;
        const categoriesMap = {};
        questionOnly.forEach(q => {
          const cat = q.category || '其他';
          categoriesMap[cat] = (categoriesMap[cat] || 0) + 1;
        });
        const categories = Object.keys(categoriesMap).map(k => ({ category: k, count: categoriesMap[k] }));

        // 課後小組：貢獻
        const groupPostsAgg = {};
        allNodes.forEach(n => {
          const gid = n.group_id || n.Group?.id;
          if (!gid) return;
          if (!groupPostsAgg[gid]) groupPostsAgg[gid] = 0;
          if (n.node_type !== 'reply') groupPostsAgg[gid] += 1;
        });
        const groupContribution = groups.map(g => {
          const members = (g.members || []).length;
          const totalPosts = groupPostsAgg[g.id] || 0;
          const avgPostsPerMember = members > 0 ? Number((totalPosts / members).toFixed(1)) : 0;
          return {
            id: g.id,
            name: g.name,
            members,
            totalPosts,
            avgPostsPerMember,
            completionTime: '-'
          };
        });

        const newData = {
          applicationInfo: appInfo,
          realtimePersonal: {
            taskActivation: { totalStudents, activatedStudents, notActivated },
            interactionStatus,
            stickyNoteRanking,
            wallUsage: { 'idea-wall': 0, 'question-wall': 0, 'cross-group': 0, 'dashboard': 0 }
          },
          realtimeGroup: { groupInteraction },
          postClassPersonal: {
            participationStats,
            questionWallStats: { totalQuestions, categories }
          },
          postClassGroup: { groupContribution }
        };

        if (mounted) {
          setCurrentApplication(appInfo);
          setDashboardData(newData);
          studentListRef.current = studentList; // 保存學生名單供即時更新使用
        }
      } catch (err) {
        console.error('載入儀表板資料失敗:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [id]);

  // WebSocket: 監聽 analytics 事件，更新即時互動與分牆面統計
  useEffect(() => {
    if (!id) return;
    const socket = io(url.socketioHost, { path: '/s/socket.io' });
    setWs(socket);
    const channel = `analytics-event-${id}`;
    const handler = (evt) => {
      if (!evt || String(evt.course_dispatch_id) !== String(id)) return;
      const actorId = String(evt.actor_id);
      const tool = evt.tool;
      
      if (evt.type === 'ToolUseStarted') {
        if (actorId) {
          activeUsersRef.current.add(actorId);
          if (!toolUseRef.current[actorId]) {
            toolUseRef.current[actorId] = [];
          }
          toolUseRef.current[actorId].push({ tool, startedAt: new Date().getTime() });
        }
      } else if (evt.type === 'ToolUseEnded') {
        if (actorId && toolUseRef.current[actorId]) {
          // 移除該工具的記錄
          toolUseRef.current[actorId] = toolUseRef.current[actorId].filter(v => v.tool !== tool);
          // 如果該用戶沒有任何工具在使用中，從活躍用戶中移除
          if (toolUseRef.current[actorId].length === 0) {
            delete toolUseRef.current[actorId];
            activeUsersRef.current.delete(actorId);
          }
        }
      } else if (evt.type === 'SessionStarted') {
        if (actorId) activeUsersRef.current.add(actorId);
      } else if (evt.type === 'SessionEnded') {
        if (actorId) {
          activeUsersRef.current.delete(actorId);
          delete toolUseRef.current[actorId];
        }
      }

      // 計算分牆面統計
      const wallStats = {
        'idea-wall': 0,
        'question-wall': 0,
        'cross-group': 0,
        'dashboard': 0
      };
      Object.values(toolUseRef.current).forEach(tools => {
        if (Array.isArray(tools)) {
          tools.forEach(v => {
            if (v && v.tool && wallStats.hasOwnProperty(v.tool)) {
              wallStats[v.tool] += 1;
            }
          });
        }
      });

      // 依據 activeUsers 更新互動狀況（對照學生名單）
      setDashboardData(prev => {
        const total = (prev.realtimePersonal?.taskActivation?.totalStudents) || studentListRef.current.length;
        const activeCount = activeUsersRef.current.size;
        const silentCount = Math.max(total - activeCount, 0);
        const silentList = studentListRef.current
          .filter(s => !activeUsersRef.current.has(String(s.id)))
          .map(s => ({ id: s.id, name: s.name }));
        
        return {
          ...prev,
          realtimePersonal: {
            ...prev.realtimePersonal,
            interactionStatus: {
              activeStudents: activeCount,
              silentStudents: silentCount,
              silentList
            },
            wallUsage: wallStats // 新增：分牆面使用統計
          }
        };
      });
    };
    socket.on(channel, handler);
    return () => {
      socket.off(channel, handler);
      socket.disconnect();
    };
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleViewDetail = (type, data) => {
    setSelectedDetail({ type, data });
    setOpenDetailDialog(true);
  };

  // A-3: 使用分層架構選單
  const allPhaseMenuGroups = createAllPhaseMenuGroups('implementation', 'dashboard', navigate, id);

  return (
    <div className="teacher-layout class-application-dashboard-container">
      <div className="main-content-v2">
        <Navbar 
          allPhaseMenuGroups={allPhaseMenuGroups}
          currentPhase="implementation"
          currentCourseId={id}
          onPhaseChange={(phase) => {
            if (phase === 'preparation') {
              navigate('/teacher/preparation');
            } else if (phase === 'implementation') {
              navigate('/teacher/implementation');
            } else if (phase === 'diagnosis') {
              navigate('/teacher/diagnosis');
            }
          }}
          onCourseChange={(newCourseId) => {
            const currentPath = window.location.pathname;
            const newPath = currentPath.replace(/\/\d+(\/|$)/, `/${newCourseId}$1`);
            navigate(newPath);
          }}
        />
        
        <div className="class-header-row">
          <h1 className="main-title">學習儀表板</h1>
        </div>

        {/* 分析維度標籤 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab 
              label="即時個人追蹤" 
              icon={<PersonIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="即時小組追蹤" 
              icon={<GroupIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="課後個人診斷" 
              icon={<AssessmentIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="課後小組診斷" 
              icon={<TrendingUpIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* 儀表板內容 */}
        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && (
            <RealtimePersonalTab data={dashboardData.realtimePersonal} onViewDetail={handleViewDetail} />
          )}
          {activeTab === 1 && (
            <RealtimeGroupTab data={dashboardData.realtimeGroup} onViewDetail={handleViewDetail} />
          )}
          {activeTab === 2 && (
            <PostClassPersonalTab data={dashboardData.postClassPersonal} onViewDetail={handleViewDetail} />
          )}
          {activeTab === 3 && (
            <PostClassGroupTab data={dashboardData.postClassGroup} onViewDetail={handleViewDetail} />
          )}
        </Box>

        {/* 詳細資訊對話框 */}
        <DetailDialog 
          open={openDetailDialog}
          detail={selectedDetail}
          onClose={() => {
            setOpenDetailDialog(false);
            setSelectedDetail(null);
          }}
        />
      </div>
    </div>
  );
}

// 即時個人追蹤標籤頁
function RealtimePersonalTab({ data, onViewDetail }) {
  return (
    <Grid container spacing={3}>
      {/* 任務啟動狀況 */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              任務啟動狀況
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" color="primary" sx={{ mr: 1 }}>
                {data.taskActivation.activatedStudents}/{data.taskActivation.totalStudents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                學生已啟動任務
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={(data.taskActivation.activatedStudents / data.taskActivation.totalStudents) * 100}
              sx={{ mb: 2 }}
            />
            <Button 
              size="small" 
              onClick={() => onViewDetail('taskActivation', data.taskActivation)}
            >
              查看未啟動學生
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* 互動狀況 */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              互動狀況
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" color="success.main" sx={{ mr: 1 }}>
                {data.interactionStatus.activeStudents}/{data.interactionStatus.activeStudents + data.interactionStatus.silentStudents}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                學生有互動
              </Typography>
            </Box>
            <Button 
              size="small" 
              onClick={() => onViewDetail('interactionStatus', data.interactionStatus)}
            >
              查看沉默學生
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* 牆面使用情況 */}
      {data.wallUsage && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                牆面使用情況（即時）
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="h4" color="primary.contrastText">
                      {data.wallUsage['idea-wall'] || 0}
                    </Typography>
                    <Typography variant="body2" color="primary.contrastText">
                      想法牆
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="h4" color="info.contrastText">
                      {data.wallUsage['question-wall'] || 0}
                    </Typography>
                    <Typography variant="body2" color="info.contrastText">
                      問題牆
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'secondary.light', borderRadius: 1 }}>
                    <Typography variant="h4" color="secondary.contrastText">
                      {data.wallUsage['cross-group'] || 0}
                    </Typography>
                    <Typography variant="body2" color="secondary.contrastText">
                      跨組討論
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="h4" color="warning.contrastText">
                      {data.wallUsage['dashboard'] || 0}
                    </Typography>
                    <Typography variant="body2" color="warning.contrastText">
                      儀表板
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* 便利貼熱度排名 */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              便利貼熱度排名
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>排名</TableCell>
                    <TableCell>內容</TableCell>
                    <TableCell>作者</TableCell>
                    <TableCell>瀏覽次數</TableCell>
                    <TableCell>互動次數</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.stickyNoteRanking.map((note, index) => (
                    <TableRow key={note.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{note.content}</TableCell>
                      <TableCell>{note.author}</TableCell>
                      <TableCell>{note.views}</TableCell>
                      <TableCell>{note.interactions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// 即時小組追蹤標籤頁
function RealtimeGroupTab({ data, onViewDetail }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              小組互動比例與進度
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>小組</TableCell>
                    <TableCell>成員數</TableCell>
                    <TableCell>活躍成員</TableCell>
                    <TableCell>互動比例</TableCell>
                    <TableCell>進度</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.groupInteraction.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.members}</TableCell>
                      <TableCell>{group.activeMembers}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={group.interactionRate}
                              color={group.interactionRate >= 80 ? 'success' : group.interactionRate >= 60 ? 'warning' : 'error'}
                            />
                          </Box>
                          <Typography variant="body2">{group.interactionRate}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={group.progress}
                              color={group.progress >= 80 ? 'success' : group.progress >= 60 ? 'warning' : 'error'}
                            />
                          </Box>
                          <Typography variant="body2">{group.progress}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => onViewDetail('groupDetail', group)}>
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// 課後個人診斷標籤頁
function PostClassPersonalTab({ data, onViewDetail }) {
  return (
    <Grid container spacing={3}>
      {/* 參與度統計 */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              學生參與度統計
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>學生</TableCell>
                    <TableCell>發言次數</TableCell>
                    <TableCell>回應次數</TableCell>
                    <TableCell>登入頻率</TableCell>
                    <TableCell>活躍度</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.participationStats.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.posts}</TableCell>
                      <TableCell>{student.responses}</TableCell>
                      <TableCell>
                        <Chip 
                          label={student.loginFrequency}
                          color={student.loginFrequency === '高' ? 'success' : student.loginFrequency === '中' ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.activityLevel}
                          color={student.activityLevel === '高' ? 'success' : student.activityLevel === '中' ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* 問題牆分類統計 */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              問題牆分類統計
            </Typography>
            <Typography variant="h4" color="primary" gutterBottom>
              {data.questionWallStats.totalQuestions}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              總問題數
            </Typography>
            {data.questionWallStats.categories.map((category, index) => (
              <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{category.category}</Typography>
                <Typography variant="body2" fontWeight="bold">{category.count}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// 課後小組診斷標籤頁
function PostClassGroupTab({ data, onViewDetail }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              小組貢獻與完成時間
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>小組</TableCell>
                    <TableCell>成員數</TableCell>
                    <TableCell>總發言數</TableCell>
                    <TableCell>平均發言數</TableCell>
                    <TableCell>完成時間</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.groupContribution.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell>{group.name}</TableCell>
                      <TableCell>{group.members}</TableCell>
                      <TableCell>{group.totalPosts}</TableCell>
                      <TableCell>{group.avgPostsPerMember}</TableCell>
                      <TableCell>{group.completionTime}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => onViewDetail('groupContribution', group)}>
                          詳細
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

// 詳細資訊對話框
function DetailDialog({ open, detail, onClose }) {
  if (!detail) return null;

  const renderDetailContent = () => {
    switch (detail.type) {
      case 'taskActivation':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>未啟動任務的學生</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>學生姓名</TableCell>
                    <TableCell>原因</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.data.notActivated.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.name}</TableCell>
                      <TableCell>{student.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      case 'interactionStatus':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>沉默學生名單</Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>學生姓名</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.data.silentList.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{student.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      default:
        return (
          <Typography>詳細資訊載入中...</Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>詳細資訊</DialogTitle>
      <DialogContent>
        {renderDetailContent()}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose}>關閉</Button>
      </DialogActions>
    </Dialog>
  );
}

